import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, theme } from 'antd';
import { Field } from '../../components/Field';
import { LocalizedContentShow } from '../../components/LocalizedContentTabs';

interface SubscriptionPlanDto {
  id: string;
  name: string;
  description?: string | null;
  translations?: Record<string, Record<string, string>> | null;
  planType: string;
  priceMonthlyCents: number;
  priceYearlyCents?: number | null;
  currency: string;
  trialDays?: number | null;
  isActive: boolean;
  sortOrder: number;
  features: string[];
  providerProductId?: string | null;
  providerPriceId?: string | null;
  createdAt: string;
  updatedAt: string;
}

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const planTypeColors: Record<string, string> = {
  FREE: '#898989',
  BASIC: '#3b82f6',
  PRO: '#8b5cf6',
  PREMIUM: '#f59e0b',
  ENTERPRISE: '#3ecf8e',
};

export const SubscriptionPlanShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<SubscriptionPlanDto>({ resource: 'subscription-plans' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="subscription-plans">
      <div style={{ padding: '8px 0' }}>
        <LocalizedContentShow
          record={item}
          fields={[
            { key: 'name', label: 'Name' },
            { key: 'description', label: 'Description', textarea: true },
          ]}
        />

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
          }}
        >
          <Field label="Plan Type">
            <Tag style={tagStyle(planTypeColors[item.planType] || '#898989')}>{item.planType}</Tag>
          </Field>
          <Field label="Monthly Price">{`$${(item.priceMonthlyCents / 100).toFixed(2)} ${item.currency}`}</Field>
          {item.priceYearlyCents != null && (
            <Field label="Yearly Price">{`$${(item.priceYearlyCents / 100).toFixed(2)} ${item.currency}`}</Field>
          )}
          {item.trialDays != null && <Field label="Trial Days">{item.trialDays}</Field>}
          <Field label="Sort Order">{item.sortOrder}</Field>
          <Field label="Active">
            <Tag style={tagStyle(item.isActive ? '#3ecf8e' : '#898989')}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </Field>
          {item.features.length > 0 && (
            <Field label="Features">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {item.features.map((f, i) => (
                  <Tag key={i} style={tagStyle('#3b82f6')}>{f}</Tag>
                ))}
              </div>
            </Field>
          )}
          {item.providerProductId && <Field label="Provider Product ID">{item.providerProductId}</Field>}
          {item.providerPriceId && <Field label="Provider Price ID">{item.providerPriceId}</Field>}
          <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
          <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
        </div>
      </div>
    </Show>
  );
};
